package no.vareflow.app.domain

/**
 * The app's languages. Norwegian Bokmål is the default; English can be chosen on the login page
 * and in Mer → Språk. The choice follows the person's account (Firestore `users/{uid}.language`)
 * and is remembered on the device for the login page.
 */
enum class AppLanguage(val code: String, val nativeName: String, val firebaseCode: String, val htmlLang: String) {
    NB("nb", "Norsk", "no", "nb"),
    EN("en", "English", "en", "en");

    companion object {
        val DEFAULT = NB

        /** "nb", "no", "nn", "nb-NO" → NB; "en", "en-GB" → EN; anything else → null. */
        fun fromCode(code: String?): AppLanguage? {
            val c = code?.trim()?.lowercase()?.substringBefore('-')?.substringBefore('_') ?: return null
            return when (c) {
                "nb", "no", "nn" -> NB
                "en" -> EN
                else -> null
            }
        }
    }
}

/**
 * The language the UI is drawn in right now. Plain state: the web redraws everything on each
 * render, and the Android app recomposes from [AppLanguage] state it keeps itself and copies here.
 */
object I18n {
    var language: AppLanguage = AppLanguage.DEFAULT
}

/** The text in the current UI language. Norwegian first, so the code still reads as before. */
fun t(nb: String, en: String): String = if (I18n.language == AppLanguage.EN) en else nb

/** The text in a given language (reports can be made in another language than the UI). */
fun tr(language: AppLanguage, nb: String, en: String): String = if (language == AppLanguage.EN) en else nb

/** Products keep their import status in Norwegian ("Klar", "Manuell"); this is the text to show. */
fun importStatusLabel(raw: String): String = when (raw.trim()) {
    "Klar" -> t("Klar", "Ready")
    "Manuell" -> t("Manuell", "Manual")
    "Kontroller" -> t("Kontroller", "To check")
    else -> raw
}
